// src/pages/Home/MapSection/MapSection.js

import React, { useEffect, useState, useRef } from 'react';
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Popup
} from 'react-leaflet';
import L from 'leaflet';
import styles from './MapSection.module.css';
import 'leaflet/dist/leaflet.css';
import customGeo from '../../../assets/images/custom.geo.json';
import { useTranslation } from 'react-i18next';

// Fix Leaflet’s default icon URLs
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl:       require('leaflet/dist/images/marker-icon.png'),
  shadowUrl:     require('leaflet/dist/images/marker-shadow.png'),
});

// Bouncing pin via DivIcon
const bouncingIcon = new L.DivIcon({
  className: styles.bouncingPin,
  html: `<div class="${styles.pin}"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 20],
});

const DEFAULT_CENTER = [42, 35]; // Istanbul
const DEFAULT_ZOOM = 3.5;

const getBoundingBoxArea = (coords = []) => {
  if (!coords.length) return 0;
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  coords.forEach(([lng, lat]) => {
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  });

  return Math.abs((maxLng - minLng) * (maxLat - minLat));
};

const getUnitedKingdomCenter = (feature) => {
  const geometry = feature?.geometry;
  if (!geometry) return null;

  if (geometry.type === 'Polygon') {
    const layer = L.geoJSON(feature);
    const center = layer.getBounds().getCenter();
    return [center.lat, center.lng];
  }

  if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates)) {
    let largestPolygon = null;
    let largestArea = 0;

    geometry.coordinates.forEach((polygon) => {
      const outerRing = polygon?.[0] || [];
      const area = getBoundingBoxArea(outerRing);
      if (area > largestArea) {
        largestArea = area;
        largestPolygon = polygon;
      }
    });

    if (largestPolygon) {
      const ukMainland = {
        type: 'Feature',
        properties: feature.properties || {},
        geometry: {
          type: 'Polygon',
          coordinates: largestPolygon
        }
      };
      const layer = L.geoJSON(ukMainland);
      const center = layer.getBounds().getCenter();
      return [center.lat, center.lng];
    }
  }

  return null;
};

const MapSection = ({ locations = [] }) => {
  const { t } = useTranslation();
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [countryCenters, setCountryCenters] = useState([]);
  const [mergedGeo, setMergedGeo] = useState(customGeo);
  const mapRef = useRef(null);

  // Center map on first provided location
  useEffect(() => {
    if (locations.length) {
      setMapCenter([locations[0].lat, locations[0].lng]);
    }
  }, [locations]);

  // Merge UK polygon at runtime if missing, then compute centroids
  useEffect(() => {
    const hasUnitedKingdom = mergedGeo.features.some(
      f => (f.properties && (f.properties.name === 'United Kingdom' || f.properties.admin === 'United Kingdom'))
    );

    if (!hasUnitedKingdom) {
      const url = 'https://geojson.xyz/naturalearth-110m/admin_0_countries.geojson';
      fetch(url)
        .then(res => res.json())
        .then(data => {
          const uk = data.features.find(
            f => (f.properties && (f.properties.name === 'United Kingdom' || f.properties.admin === 'United Kingdom'))
          );
          if (uk) {
            setMergedGeo(prev => ({
              ...prev,
              features: [...prev.features, uk]
            }));
          }
        })
        .catch(() => {
          // ignore network errors; base map still renders
        });
    }

    const centers = (hasUnitedKingdom ? mergedGeo.features : customGeo.features).map((feature, idx) => {
      const layer = L.geoJSON(feature);
      const bounds = layer.getBounds();
      const c = bounds.getCenter();
      const name = (feature.properties && (feature.properties.name || feature.properties.admin)) || `country-${idx}`;
      const isUnitedKingdom = name === 'United Kingdom';
      const ukCenter = isUnitedKingdom ? getUnitedKingdomCenter(feature) : null;
      return {
        id: name,
        position: ukCenter || [c.lat, c.lng],
        label: name
      };
    });
    setCountryCenters(centers);
  }, [mergedGeo]);

  // Clean up Leaflet map on unmount to avoid "already initialized" errors
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const countryStyle = {
    color: '#0056d2',
    weight: 2,
    fillColor: '#cfe8ff',
    fillOpacity: 0.3
  };

  return (
    <section className={styles.wrapper}>
      <h2 className={styles.title}>{t('home.global_services')}</h2>
      <MapContainer
        center={mapCenter}
        zoom={DEFAULT_ZOOM}
        scrollWheelZoom={false}
        zoomControl={true}
        className={styles.map}
        // Capture the Leaflet instance
        whenCreated={mapInstance => {
          mapRef.current = mapInstance;
        }}
      >
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <GeoJSON data={mergedGeo} style={countryStyle} />

        {countryCenters.map(({ id, position, label }) => (
          <Marker
            key={id}
            position={position}
            icon={bouncingIcon}
          >
            <Popup>{label}</Popup>
          </Marker>
        ))}

      </MapContainer>
    </section>
  );
};

export default React.memo(MapSection);
